<script setup lang="ts">
import ValidatedInput from "@/builder/components/input/ValidatedInput.vue";
import { FieldStyle } from "@/builder/enums";
import { useRenderStore } from "@/builder/stores/renderStore";
import { computed, inject, PropType, Ref } from "vue";
import { PageDesign } from "../page/PageInterface";
import { INameInputCustomData, ITextInputComponent } from "./TextInputInterface";

const renderStore = useRenderStore();

const props = defineProps({
    component: {
        type: Object as PropType<ITextInputComponent>,
        required: true,
    },
});

const pageDesign = inject("pageDesign") as Ref<PageDesign>;
const fieldStyle = computed(() => {
    return pageDesign.value.fieldStyle;
});

const customData = computed(() => {
    return props.component.customData as INameInputCustomData;
});
</script>

<template>
    <div class="form-row name-container" :class="{ 'rd-gap-x-4': fieldStyle != FieldStyle.Compact }">
        <template v-if="customData.title">
            <ValidatedInput :id="component.id" :value="renderStore.donor.title" :label="customData.titleLabel"
                name="Donor.Title" class="rd-w-full md:rd-w-1/6" :isRequired="false" :maxLength="75">
                <template #input="inputProps">
                    <input type="text" v-model="renderStore.donor.title" :placeholder="customData.titlePlaceholder"
                        :aria-describedby="inputProps.describedBy" :id="component.id + '_Donor.Title'"
                        autocomplete="honorific-prefix" />
                </template>
            </ValidatedInput>
        </template>

        <ValidatedInput :id="component.id" :value="renderStore.donor.firstName" :label="customData.firstNameLabel"
            name="Donor.FirstName" class="rd-w-full md:rd-flex-equal"
            :class="{ 'rd-mt-4 md:rd-mt-0': customData.title && fieldStyle != FieldStyle.Compact }"
            :isRequired="component.customData.required" :maxLength="75">

            <template #input="inputProps">
                <input type="text" v-model="renderStore.donor.firstName" :placeholder="customData.firstNamePlaceholder"
                    :aria-describedby="inputProps.describedBy" :aria-invalid="inputProps.isInvalid" aria-required="true"
                    :id="component.id + '_Donor.FirstName'" autocomplete="given-name" />
            </template>
        </ValidatedInput>

        <template v-if="customData.middleName">
            <ValidatedInput :id="component.id" :value="renderStore.donor.middleName" :label="customData.middleNameLabel"
                name="Donor.MiddleName" class="rd-w-full md:rd-flex-equal"
                :class="{ 'rd-mt-4 md:rd-mt-0': fieldStyle != FieldStyle.Compact }" :isRequired="false" :maxLength="75">
                <template #input="inputProps">
                    <input type="text" v-model="renderStore.donor.middleName"
                        :placeholder="customData.middleNamePlaceholder" :aria-describedby="inputProps.describedBy"
                        :id="component.id + '_Donor.MiddleName'" autocomplete="additional-name" />
                </template>
            </ValidatedInput>
        </template>

        <ValidatedInput :id="component.id" :value="renderStore.donor.lastName" :label="customData.lastNameLabel"
            name="Donor.LastName" class="rd-w-full md:rd-flex-equal"
            :class="{ 'rd-mt-4 md:rd-mt-0': fieldStyle != FieldStyle.Compact }"
            :isRequired="component.customData.required" :maxLength="75">
            <template #input="inputProps">
                <input type="text" v-model="renderStore.donor.lastName" :placeholder="customData.lastNamePlaceholder"
                    :aria-describedby="inputProps.describedBy" aria-required="true"
                    :id="component.id + '_Donor.LastName'" autocomplete="family-name" />
            </template>
        </ValidatedInput>

        <template v-if="customData.suffix">
            <ValidatedInput :id="component.id" :value="renderStore.donor.suffix" :label="customData.suffixLabel"
                name="Donor.Suffix" class="rd-w-full md:rd-w-1/6"
                :class="{ 'rd-mt-4 md:rd-mt-0': fieldStyle != FieldStyle.Compact }" :isRequired="false" :maxLength="75">
                <template #input="inputProps">
                    <input type="text" v-model="renderStore.donor.suffix" :placeholder="customData.suffixPlaceholder"
                        :aria-describedby="inputProps.describedBy" :id="component.id + '_Donor.Suffix'"
                        autocomplete="honorific-suffix" />
                </template>
            </ValidatedInput>
        </template>
    </div>
</template>
